import { components } from 'react-select';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { SelectComponentsConfig } from 'react-select/dist/declarations/src/components';
import { GroupBase } from 'react-select/dist/declarations/src/types';
import {
	ArrayElement, Avatar, AvatarSize, getAvatarSource,
} from '@aleph/shared-components';
import { UserGroupOption } from 'wb-reddit/app/routes/users/mutator/utils';
import {
	CreateUserGroupMutationVariables, RedditUserForGroupQuery,
	RedditUserForGroupQueryUsers,
	RedditUserGroupsListQuery,
	RedditUserGroupsListQueryUser,
	UpdateUserGroupMutationVariables, UserGroupRole,
	UserGroupType,
} from 'wb-reddit/graphql/generated';
import { ReactNode } from 'react';
import { DefaultUserMetaData } from 'wb-reddit/app/common/hooks/useUserMetaData';
import { TeamRow, TeamUser } from 'wb-reddit/app/routes/teams/container';
import { mapTypeNameToAvatarEntity } from 'wb-reddit/app/common/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';

export type UserGroupsType = ArrayElement<RedditUserGroupsListQuery['userGroups']['items']>;
export type UserGroupClientAccessType = ArrayElement<UserGroupsType['clients']>;
export type UserGroupAgencyAdAccountsAccessType = ArrayElement<UserGroupsType['agencyAccounts']>;
export type UserOptionType = ArrayElement<RedditUserForGroupQuery['users']>;
export type UserGroupUserOptionType = ArrayElement<UserGroupsType['users']>;

export type MutatingUserGroupType = (UpdateUserGroupMutationVariables | CreateUserGroupMutationVariables) & {
	userGroupId?: string
	users?: UserGroupUserOptionType[] | null
	clients?: UserGroupClientAccessType[] | null
	agencyAccounts?: UserGroupAgencyAdAccountsAccessType[] | null
};

export const newTeamData: TeamRow = {
	userGroupId:   '',
	salesforceId:  '',
	userGroupName: '',
	users:         [],
	id:            '',
	userGroupType: UserGroupType.Public,
};

interface ComponentProps {
	control?: {
		suffix?: ReactNode
	}
}

type OwnerUserAutocompleteComponentsReturn =
	(props?: ComponentProps) => SelectComponentsConfig<User, false, GroupBase<User>>;

export const ownerUserAutocompleteComponents: OwnerUserAutocompleteComponentsReturn = (componentProps) => ({
	Option: ({ children, ...props }) => (
		<components.Option
			className={
				classNames(
					props.className,
					'!cursor-pointer overflow-hidden !flex !items-center gap-[8px] !text-black hover:!bg-gray-100',
					{
						'!bg-gray-100': props.isFocused,
						'!bg-gray-200': props.isSelected,
					},
				)
			}
			{...props}
		>
			<Avatar
				size={AvatarSize.XXS}
				src={getAvatarSource(props.data)}
				entityType={mapTypeNameToAvatarEntity(props.data)}
				entityUserInitials={getUserInitials(props.data)}
			/>
			<div className="overflow-hidden overflow-ellipsis">{children}</div>
		</components.Option>
	),
	SingleValue: ({ children, ...props }) => (
		<components.SingleValue
			{...props}
			className={classNames(props.className, '!text-[13px] !gap-[8px] !flex !items-center')}
		>
			<Avatar
				size={AvatarSize.XXS}
				src={getAvatarSource(props.data)}
				entityType={mapTypeNameToAvatarEntity(props.data)}
				entityUserInitials={getUserInitials(props.data)}
			/>
			<div>{children}</div>
		</components.SingleValue>
	),
	Placeholder: () => null,
	Control:     ({ children, ...props }) => (
		<components.Control
			className={classNames(
				props.className,
				'relative flex h-[32px] !min-h-[32px] !cursor-text !border-gray-300 !bg-white',
			)}
			{...props}
		>
			<motion.label
				className={classNames('absolute bg-white flex items-center left-[2px] px-[4px]', {
					'text-[11px]':   props?.hasValue || props.menuIsOpen,
					'text-[13px]':   !props?.hasValue && !props.menuIsOpen,
					'text-gray-600': props.isDisabled,
				})}
				initial={{
					height: props?.hasValue || props.menuIsOpen ? 'auto' : '100%',
					top:    props?.hasValue || props.menuIsOpen ? -9 : 0,
				}}
				animate={{
					height: props?.hasValue || props.menuIsOpen ? 'auto' : '100%',
					top:    props?.hasValue || props.menuIsOpen ? -9 : 0,
				}}
				transition={{
					duration: 0.1,
				}}
			>
				Owner
			</motion.label>
			{children}
			{
				componentProps?.control?.suffix	&& (
					<div className="flex items-center justify-center text-gray-950 gap-[8px] w-fit pr-2">
						{componentProps?.control?.suffix}
					</div>
				)
			}
		</components.Control>
	),
});

export const usersAutocompleteComponents: SelectComponentsConfig<
TeamUser,
true,
GroupBase<TeamUser>
> = {
	ClearIndicator:      () => null,
	MultiValueContainer: () => null,
	Placeholder:         () => null,
	Option:              ({ children, ...rest }) => (
		<components.Option
			{...rest}
			className={
				classNames(
					rest.className,
					'!text-[18px] !text-black !h-[48px] overflow-hidden !flex whitespace-nowrap gap-[8px]',
					'items-center hover:!bg-gray-100 border-b border-gray-200 last:border-0',
					{
						'!bg-gray-100': rest.isFocused,
						'!bg-gray-200': rest.isSelected,
					},
				)
			}
		>
			<Avatar
				src={getAvatarSource(rest.data?.user)}
				entityType={mapTypeNameToAvatarEntity(rest.data?.user)}
				entityUserInitials={getUserInitials(rest.data?.user)}
			/>
			<div className="overflow-hidden overflow-ellipsis">{children}</div>
		</components.Option>
	),
	Input: ({ children, ...rest }) => (
		<components.Input
			{...rest}
			className={classNames(rest.className, 'text-[18px] [&>input]:!w-[250px]')}
			placeholder={rest.selectProps?.placeholder?.toString?.() ?? ''}
		>
			{children}
		</components.Input>
	),
};

// TODO provide for spotify when BE is ready
export const getTeamAccessEntities = (group?: TeamRow) => [
	...group?.clients ?? [],
	...group?.agencyAccounts ?? [],
].map((accessEntity) => ({
	name: accessEntity.name ?? (accessEntity as UserGroupClientAccessType).secondaryName,
	type: accessEntity.__typename,
}));

export const getGroupOwner = (group: UserGroupOption) => group.users
	?.find?.(groupUser => groupUser?.userGroupUserRole === UserGroupRole.Owner)?.user;

export type User = RedditUserForGroupQueryUsers & {
	userName?: string | null
	userSurname?: string | null
};

export function parseTeamOwnerFromPlatformUser(platformUser?: DefaultUserMetaData): User | null {
	if (!platformUser) return null;

	return {
		userId:                platformUser?.userId,
		userEmail:             platformUser?.userEmail,
		userFullName:          platformUser?.userFullName,
		profilePictureUrl:     platformUser?.userProfilePictureUrl,
		userProfilePictureUrl: platformUser?.userProfilePictureUrl,
		userAccountLevel:      platformUser?.userAccountLevel,
		id:                    platformUser.id,
		userName:              platformUser?.userName,
		__typename:            'SearchUser',
	};
}

export function parseTeamOwnerFromFromQueryUser(user?: RedditUserGroupsListQueryUser) {
	return {
		userId:                user?.userId,
		userEmail:             user?.userEmail,
		userFullName:          user?.userFullName,
		profilePictureUrl:     user?.userProfilePictureUrl,
		userProfilePictureUrl: user?.userProfilePictureUrl,
		userAccountLevel:      user?.userAccountLevel,
		id:                    user?.id,
		userName:              user?.userName,
		__typename:            'SearchUser',
	// In the BE ids are set as nullable, but IDs should not be null, so that is why this is typecasted
	} as User;
}
